import { AppLayout } from "@Layouts";
import { createRoute, redirect } from "@tanstack/react-router";
import { HomePage } from "@Elements";
import { Authentication, Root } from "@Routes";
import {
  CorporationAddRoute,
  CorporationBaseRoute,
  CorporationChatBotRoute,
  CorporationEditRoute,
  CorporationListRoute,
  CorporationPersonnelListRoute,
  CorporationProductListRoute,
} from "./Corporation.route";
import {
  FinancialBaseRoute,
  FinancialExchangeRateAddRoute,
  FinancialExchangeRateBaseRoute,
  FinancialExchangeRateEditRoute,
  FinancialExchangeRateListRoute,
  FinancialMembershipTypeAddRoute,
  FinancialMembershipTypeBaseRoute,
  FinancialMembershipTypeEditRoute,
  FinancialMembershipTypeListRoute,
  FinancialPaymentTypeAddRoute,
  FinancialPaymentTypeBaseRoute,
  FinancialPaymentTypeEditRoute,
  FinancialPaymentTypeListRoute,
  FinancialProductTransactionAddRoute,
  FinancialProductTransactionBaseRoute,
  FinancialProductTransactionEditRoute,
  FinancialProductTransactionListRoute,
  FinancialTimestampTransactionAddRoute,
  FinancialTimestampTransactionBaseRoute,
  FinancialTimestampTransactionCorporationListRoute,
  FinancialTimestampTransactionEditRoute,
  FinancialTimestampTransactionListRoute,
  FinancialTimestampTransactionReportListRoute,
} from "./Financial.route";
import {
  ProductBaseRoute,
  ProductPricesAddRoute,
  ProductPricesBaseRoute,
  ProductPricesEditRoute,
  ProductPricesListRoute,
  ProductProductPriceEditRoute,
  ProductProductsAddRoute,
  ProductProductsBaseRoute,
  ProductProductsEditRoute,
  ProductProductsListRoute,
  ProductUserScaleAddRoute,
  ProductUserScaleBaseRoute,
  ProductUserScaleEditRoute,
  ProductUserScaleListRoute,
} from "./Product.route";
import {
  CloudBaseRoute,
  CloudChatBotAddRoute,
  CloudChatBotBaseRoute,
  CloudChatBotEditRoute,
  CloudChatBotListRoute,
  CloudChatBotReportRoute,
  CloudLanguageAddRoute,
  CloudLanguageBaseRoute,
  CloudLanguageEditRoute,
  CloudLanguageListRoute,
  CloudPartnerAddRoute,
  CloudPartnerBaseRoute,
  CloudPartnerEditRoute,
  CloudPartnerListRoute,
  CloudPermissionBaseRoute,
  CloudPermissionDetailRoute,
  CloudRoleTemplateAddRoute,
  CloudRoleTemplateBaseRoute,
  CloudRoleTemplateEditRoute,
  CloudRoleTemplateListRoute,
} from "./Cloud.route";
import {
  ContactBaseRoute,
  ContactDetailRoute,
  ContactListRoute,
} from "./Contact.route";
import {
  SupportTicketBaseRoute,
  SupportTicketDetailRoute,
  SupportTicketListRoute,
} from "./SupportTicket.route";
import {
  UserManagementBaseRoute,
  UserManagementPermissionBaseRoute,
  UserManagementPermissionEditRoute,
  UserManagementPermissionListRoute,
  UserManagementRoleAddRoute,
  UserManagementRoleBaseRoute,
  UserManagementRoleEditRoute,
  UserManagementRoleListRoute,
  UserManagementUserAddRoute,
  UserManagementUserBaseRoute,
  UserManagementUserEditRoute,
  UserManagementUserListRoute,
} from "./UserManagement.route";
import { GamificationRoute } from "./Gamification.route";

export const DashboardBaseRoute = createRoute({
  getParentRoute: () => Root.RootRoute,
  id: "DashboardBaseRoute",
  component: AppLayout,
  beforeLoad: async ({ context, location }) => {
    if (!context.authentication?.isAuthenticated) {
      throw redirect({
        to: Authentication.LoginRoute.fullPath,
        search: {
          redirect: location.href,
        },
      });
    }
  },
});

const DashboardHomeRoute = createRoute({
  getParentRoute: () => DashboardBaseRoute,
  path: "/",
  component: HomePage,
  staticData: {
    TITLE: "Dashboard",
  },
});

export const DashboardRouteTree = DashboardBaseRoute.addChildren([
  DashboardHomeRoute,
  CorporationBaseRoute,
  FinancialBaseRoute,
  ProductBaseRoute,
  CloudBaseRoute,
  UserManagementBaseRoute,
  ContactBaseRoute,
  SupportTicketBaseRoute,
  GamificationRoute,
]);

const Routes = {
  Home: {
    DashboardBaseRoute,
    DashboardHomeRoute,
  },
  Corporation: {
    CorporationBaseRoute,
    CorporationListRoute,
    CorporationAddRoute,
    CorporationEditRoute,
    CorporationProductListRoute,
    CorporationPersonnelListRoute,
    CorporationChatBotRoute,
  },
  Financial: {
    FinancialBaseRoute,
    FinancialProductTransactionBaseRoute,
    FinancialTimestampTransactionBaseRoute,
    FinancialPaymentTypeBaseRoute,
    FinancialMembershipTypeBaseRoute,
    FinancialExchangeRateBaseRoute,
    FinancialProductTransactionListRoute,
    FinancialTimestampTransactionReportListRoute,
    FinancialProductTransactionAddRoute,
    FinancialProductTransactionEditRoute,
    FinancialTimestampTransactionListRoute,
    FinancialTimestampTransactionCorporationListRoute,
    FinancialTimestampTransactionAddRoute,
    FinancialTimestampTransactionEditRoute,
    FinancialPaymentTypeListRoute,
    FinancialPaymentTypeAddRoute,
    FinancialPaymentTypeEditRoute,
    FinancialMembershipTypeListRoute,
    FinancialMembershipTypeAddRoute,
    FinancialMembershipTypeEditRoute,
    FinancialExchangeRateListRoute,
    FinancialExchangeRateAddRoute,
    FinancialExchangeRateEditRoute,
  },
  Product: {
    ProductBaseRoute,
    ProductProductsBaseRoute,
    ProductPricesBaseRoute,
    ProductUserScaleBaseRoute,
    ProductProductsListRoute,
    ProductProductsAddRoute,
    ProductProductsEditRoute,
    ProductProductPriceEditRoute,
    ProductPricesListRoute,
    ProductPricesEditRoute,
    ProductPricesAddRoute,
    ProductUserScaleListRoute,
    ProductUserScaleAddRoute,
    ProductUserScaleEditRoute,
  },
  Cloud: {
    CloudBaseRoute,
    CloudRoleTemplateBaseRoute,
    CloudRoleTemplateListRoute,
    CloudRoleTemplateAddRoute,
    CloudRoleTemplateEditRoute,
    CloudPermissionBaseRoute,
    CloudPermissionDetailRoute,
    CloudLanguageBaseRoute,
    CloudLanguageListRoute,
    CloudLanguageAddRoute,
    CloudLanguageEditRoute,
    CloudChatBotBaseRoute,
    CloudChatBotListRoute,
    CloudChatBotAddRoute,
    CloudChatBotEditRoute,
    CloudChatBotReportRoute,
    CloudPartnerBaseRoute,
    CloudPartnerListRoute,
    CloudPartnerAddRoute,
    CloudPartnerEditRoute,
  },
  UserManagement: {
    UserManagementBaseRoute,
    UserManagementUserBaseRoute,
    UserManagementPermissionBaseRoute,
    UserManagementRoleBaseRoute,
    UserManagementUserListRoute,
    UserManagementUserAddRoute,
    UserManagementUserEditRoute,
    UserManagementRoleListRoute,
    UserManagementRoleAddRoute,
    UserManagementRoleEditRoute,
    UserManagementPermissionListRoute,
    UserManagementPermissionEditRoute,
  },
  Contact: {
    ContactBaseRoute,
    ContactListRoute,
    ContactDetailRoute,
  },
  SupportTicket: {
    SupportTicketBaseRoute,
    SupportTicketListRoute,
    SupportTicketDetailRoute,
  },
  Gamification: {
    GamificationRoute,
  },
};

export { Routes };
